import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { sendEmailVerification } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import { fbAuth, fbError } from "../lib/firebase";
import { VerifyGate } from "./VerifyGate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { MailCheck, Smartphone, CheckCircle2, Loader2, FileDown } from "lucide-react";
import { track } from "../lib/analytics";
import { toast } from "sonner";

export const isVerifiedUser = (user) =>
  !!user && (user.is_demo || user.role === "admin" || (user.email_verified && user.phone_verified));

export default function PdfGateDialog({ open, onOpenChange, onVerified }) {
  const { user, fbUser, exchangeFirebase } = useAuth();
  const [otpOpen, setOtpOpen] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const next = encodeURIComponent(location.pathname);
  const emailDone = !!user?.email_verified;
  const phoneDone = !!user?.phone_verified;
  const verified = isVerifiedUser(user);

  // Fully verified while the gate is open → close and hand back control (auto-download)
  useEffect(() => {
    if (open && verified) {
      onOpenChange(false);
      onVerified?.();
    }
  }, [open, verified, onOpenChange, onVerified]);

  // Auto-poll: detect the email verification click, then sync it to the backend
  useEffect(() => {
    if (!open || !user || emailDone || !fbUser) return;
    const t = setInterval(async () => {
      try {
        await fbAuth.currentUser?.reload();
        if (fbAuth.currentUser?.emailVerified) {
          await exchangeFirebase(fbAuth.currentUser, null, true);
          track("email_verified", { source: "pdf_gate" });
          toast.success("Email verified ✓");
        }
      } catch (e) {
        console.debug("pdf-gate: email verification poll failed", e);
      }
    }, 4000);
    return () => clearInterval(t);
  }, [open, user, emailDone, fbUser, exchangeFirebase]);

  useEffect(() => {
    if (emailCooldown <= 0) return;
    const t = setTimeout(() => setEmailCooldown(emailCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [emailCooldown]);

  const resendEmail = async () => {
    if (!fbAuth.currentUser) return;
    try {
      await sendEmailVerification(fbAuth.currentUser);
      setEmailCooldown(60);
      toast.success("Verification email sent — check your inbox (and spam)");
    } catch (e) {
      toast.error(fbError(e));
    }
  };

  return (
    <>
      <Dialog open={open && !otpOpen} onOpenChange={onOpenChange}>
        <DialogContent aria-describedby={undefined} data-testid="pdf-gate-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <FileDown size={18} className="text-[#1D4ED8]" /> Verify to download the branded PDF
            </DialogTitle>
          </DialogHeader>

          {!user ? (
            <>
              <p className="text-sm text-[#475569]">
                Your roadmap stays fully visible. To download it as a Solarian-branded PDF, create a free account and verify your email &amp; mobile — one-time only.
              </p>
              <div className="space-y-2 mt-2">
                <button onClick={() => navigate(`/signup?next=${next}`)} className="w-full bg-[#1D4ED8] text-white rounded-md py-3 text-sm font-bold hover:bg-[#1638A8]" data-testid="pdf-gate-signup-btn">
                  Create free account
                </button>
                <button onClick={() => navigate(`/login?next=${next}`)} className="w-full border border-[#0B1F3A]/30 rounded-md py-2.5 text-sm font-medium text-[#0B1F3A]" data-testid="pdf-gate-login-btn">
                  I already have an account
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-[#475569]">Two quick one-time checks — then this and every future PDF downloads instantly.</p>

              <div className={`mt-2 border rounded-lg p-3 ${emailDone ? "border-[#059669]/30 bg-[#059669]/5" : "border-[#0B1F3A]/15 bg-white"}`} data-testid="pdf-gate-step-email">
                <div className="flex items-center gap-2">
                  {emailDone ? <CheckCircle2 size={16} className="text-[#059669]" /> : <MailCheck size={16} className="text-[#1D4ED8]" />}
                  <p className="text-sm font-bold text-[#0B1F3A]">Step 1 — Verify your email</p>
                  {emailDone && <span className="ml-auto text-xs font-bold text-[#059669]">Done ✓</span>}
                </div>
                {!emailDone && (
                  <>
                    {fbUser ? (
                      <>
                        <p className="text-xs text-[#475569] mt-2 flex items-center gap-1.5">
                          <Loader2 size={12} className="animate-spin shrink-0" />
                          Link sent to <span className="font-semibold">{user.email}</span> — click it and we'll detect it here automatically.
                        </p>
                        <button onClick={resendEmail} disabled={emailCooldown > 0} className="mt-2 text-xs font-medium text-[#1D4ED8] underline disabled:opacity-50 disabled:no-underline" data-testid="pdf-gate-email-resend-btn">
                          {emailCooldown > 0 ? `Resend email (${emailCooldown}s)` : "Resend verification email"}
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-[#475569] mt-2" data-testid="pdf-gate-relogin-note">Your session needs a refresh — log out and sign in again to verify.</p>
                    )}
                  </>
                )}
              </div>

              <div className={`mt-2 border rounded-lg p-3 ${phoneDone ? "border-[#059669]/30 bg-[#059669]/5" : emailDone ? "border-[#0B1F3A]/15 bg-white" : "border-[#0B1F3A]/10 bg-[#F5F8FF] opacity-70"}`} data-testid="pdf-gate-step-phone">
                <div className="flex items-center gap-2">
                  {phoneDone ? <CheckCircle2 size={16} className="text-[#059669]" /> : <Smartphone size={16} className="text-[#059669]" />}
                  <p className="text-sm font-bold text-[#0B1F3A]">Step 2 — Verify your mobile (OTP)</p>
                  {phoneDone && <span className="ml-auto text-xs font-bold text-[#059669]">Done ✓</span>}
                </div>
                {!phoneDone && (
                  <button onClick={() => setOtpOpen(true)} disabled={!emailDone || !fbUser}
                    className="mt-2 w-full bg-[#059669] text-white rounded-md py-2.5 text-sm font-bold disabled:opacity-50" data-testid="pdf-gate-phone-btn">
                    {emailDone ? "Verify mobile number" : "Complete Step 1 first"}
                  </button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <VerifyGate open={otpOpen} onOpenChange={setOtpOpen} onVerified={() => setOtpOpen(false)} />
    </>
  );
}
